import { Trans } from '@lingui/react/macro';
import { ArrowPathRoundedSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button, cn } from '@pantry/ui';

interface MultiSelectActionBarProps {
  /** Number of selected items */
  selectedCount: number;
  /** Whether merge is possible (same product, 2+ selected) */
  canMerge: boolean;
  /** Called when merge button is pressed */
  onMerge: () => void;
  /** Called when cancel button is pressed */
  onCancel: () => void;
}

/**
 * Bottom action bar displayed during multi-select mode.
 * Shows selection count and Merge/Cancel actions.
 */
export function MultiSelectActionBar({
  selectedCount,
  canMerge,
  onMerge,
  onCancel,
}: MultiSelectActionBarProps) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg animate-in slide-in-from-bottom duration-200">
      <div className="max-w-2xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Selection count */}
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">{selectedCount}</span>
            </div>
            <span className="text-sm text-foreground">
              {selectedCount === 1 ? <Trans>item selected</Trans> : <Trans>items selected</Trans>}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Cancel button */}
            <Button variant="outline" size="sm" className="h-9 px-4 rounded-lg" onClick={onCancel}>
              <XMarkIcon className="h-4 w-4 mr-1.5" />
              <Trans>Cancel</Trans>
            </Button>

            {/* Merge button */}
            <Button
              variant="default"
              size="sm"
              className={cn('h-9 px-4 rounded-lg', !canMerge && 'opacity-50 cursor-not-allowed')}
              onClick={onMerge}
              disabled={!canMerge}
            >
              <ArrowPathRoundedSquareIcon className="h-4 w-4 mr-1.5" />
              <Trans>Merge</Trans>
            </Button>
          </div>
        </div>

        {/* Helper text */}
        {selectedCount >= 2 && !canMerge && (
          <p className="text-xs text-expiring-soon text-center mt-2">
            <Trans>Cannot merge items with different products or units</Trans>
          </p>
        )}
        {selectedCount < 2 && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            <Trans>Select 2 or more items of the same product to merge</Trans>
          </p>
        )}
      </div>
    </div>
  );
}
